/**
 * LICENSE domain state — the commercial licence, its activity log, and public
 * verification.
 *
 * ============================================================================
 * VERIFICATION IS NOT TRUSTWORTHY. See src/api/licenseApi.ts: the check compares
 * a query against the *locally derived* licence number for the *currently
 * signed-in* account, so it can only confirm your own licence to yourself. A
 * third party following a verification link gets "not found".
 *
 * Making this real is the highest-value backend item in the licensing area — a
 * verification URL that only its owner can use has no purpose.
 * ============================================================================
 */
import { create } from 'zustand';
import { apiErrorMessage } from '../api/errors';
import { licenseApi, type LocalLicenseContext } from '../api/licenseApi';
import { newId } from '../lib/ids';
import type {
  License,
  LicenseAgreementHistoryItem,
  LicensedSong,
  LicenseVerificationState,
  Song,
} from '../types/domain';

/** One row in the licence activity log. */
export interface LicenseActivity {
  id: string;
  label: string;
  /** Event kind: 'purchase' | 'generate' | 'download' | 'verify'. */
  type: string;
  date: string;
}

/** One certificate download record. */
export interface CertificateDownload {
  date: string;
  version: string;
}

/** How many activity entries are retained. */
const ACTIVITY_LIMIT = 20;

interface LicenseState {
  version: string;
  /** Human-formatted date the licence terms were last updated. */
  updatedDate: string;
  activity: LicenseActivity[];
  downloadHistory: CertificateDownload[];
  /** The current owner's licence record, when one has been loaded. */
  license: License | null;
  /** Songs the owner can show as covered by their licence. */
  licensedSongs: LicensedSong[];
  /** Agreement versions the owner accepted. */
  agreementHistory: LicenseAgreementHistoryItem[];
  /** True while the certificate is shown zoomed. */
  certificateZoomed: boolean;
  verifyQuery: string;
  verification: LicenseVerificationState;

  addActivity: (label: string, type: string) => void;
  /** Records the purchase and seeds the activity log. */
  notePurchase: (date: string) => void;
  /** Records a certificate download or print. */
  noteCertificateDownload: () => void;
  setCertificateZoomed: (zoomed: boolean) => void;
  setVerifyQuery: (query: string) => void;
  /**
   * Runs PUBLIC verification by licence number, optionally checking a specific song.
   *
   * Never rejects: a failed lookup lands in `verification` as `'failed'`, which is a
   * different fact from `'invalid'`. See the type in types/domain.ts.
   */
  verify: (input: { licenseNumber: string; title?: string; slug?: string }) => Promise<void>;
  clearVerification: () => void;
  /** Loads the signed-in owner's current licence record. */
  loadLicense: (ctx: LocalLicenseContext) => Promise<License | null>;
  /** Loads the songs currently covered by the signed-in owner's licence. */
  loadLicensedSongs: (ctx: LocalLicenseContext, songs: Song[]) => Promise<LicensedSong[]>;
  /** Loads the owner's agreement history. */
  loadAgreementHistory: (ctx: LocalLicenseContext) => Promise<LicenseAgreementHistoryItem[]>;
  reset: () => void;
}

const INITIAL = {
  version: '1.0',
  updatedDate: '',
  activity: [] as LicenseActivity[],
  downloadHistory: [] as CertificateDownload[],
  license: null as License | null,
  licensedSongs: [] as LicensedSong[],
  agreementHistory: [] as LicenseAgreementHistoryItem[],
  certificateZoomed: false,
  verifyQuery: '',
  verification: { status: 'idle' } as LicenseVerificationState,
};

export const useLicenseStore = create<LicenseState>((set, get) => ({
  ...INITIAL,

  addActivity(label, type) {
    set((s) => ({
      activity: [{ id: newId(), label, type, date: 'Just now' }, ...s.activity].slice(
        0,
        ACTIVITY_LIMIT
      ),
    }));
  },

  notePurchase(date) {
    set({ updatedDate: date, version: '1.0' });
    get().addActivity('License Purchased', 'purchase');
    get().addActivity('Certificate Generated', 'generate');
  },

  noteCertificateDownload() {
    set((s) => ({
      downloadHistory: [{ date: 'Just now', version: s.version }, ...s.downloadHistory],
    }));
    get().addActivity('Certificate Downloaded', 'download');
    void licenseApi.recordCertificateDownload();
  },

  setCertificateZoomed(certificateZoomed) {
    set({ certificateZoomed });
  },

  setVerifyQuery(verifyQuery) {
    set({ verifyQuery });
  },

  async verify(input) {
    if (!input.licenseNumber.trim()) {
      set({ verification: { status: 'idle' } });
      return;
    }
    set({ verification: { status: 'checking' } });
    try {
      const result = await licenseApi.verifyLicensePublic(input);
      set({
        verification: result.valid
          ? { status: 'valid', result: result as typeof result & { valid: true } }
          : { status: 'invalid', query: input.licenseNumber.trim() },
      });
    } catch (err) {
      /*
        A FAILED LOOKUP IS NOT AN ABSENT LICENCE, and this is the one place in the app
        where confusing the two is a legal claim rather than a cosmetic slip. Without this
        catch the state stayed at `'checking'` for ever: the Verify button remained
        disabled reading "Verifying…", no result box appeared, and the rejection escaped
        as an unhandled promise. So the screen showed neither an answer nor a failure.

        The alternative — falling through to `'invalid'` — would be worse. That renders
        "License Not Found", which tells someone whose licence is perfectly valid that it
        is not, on the public page they hand to a client as proof. `'failed'` says the
        check could not be run.
      */
      set({
        verification: {
          status: 'failed',
          // `preserveMessage: false`: this page is PUBLIC and unauthenticated, so a raw
          // `TypeError` from a runtime bug would be printed to anyone with the link.
          message: apiErrorMessage(err, { preserveMessage: false }),
        },
      });
    }
  },

  clearVerification() {
    set({ verification: { status: 'idle' } });
  },

  async loadLicense(ctx) {
    const license = await licenseApi.getMyLicense(ctx);
    set({
      license,
      version: license?.version ?? ctx.version,
      updatedDate: license?.issuedDate ?? ctx.purchaseDate,
    });
    return license;
  },

  async loadLicensedSongs(ctx, songs) {
    const page = await licenseApi.getLicensedSongs(ctx, songs);
    set({ licensedSongs: page.items });
    return page.items;
  },

  async loadAgreementHistory(ctx) {
    const agreementHistory = await licenseApi.getAgreementHistory(ctx);
    set({ agreementHistory });
    return agreementHistory;
  },

  reset() {
    set({ ...INITIAL });
  },
}));
